import { Text } from "@olivahealth/oli-ui";
import { useState, useEffect, useMemo, Suspense } from "react";
import {
  graphql,
  usePreloadedQuery,
  useQueryLoader,
  PreloadedQuery,
} from "react-relay";
import { CREATE_THREAD_TRACK_FROM } from "@olivahealth/constants";
import ConversationStarterPlatform from "@olivahealth/graphql-server/src/domain/value-objects/ConversationStarterPlatform";
import useTranslation from "../../../hooks/useTranslation";
import { useUserData } from "../../../services/contexts/UserDataContext";
import { useAmplitude } from "../../../services/contexts/AmplitudeContext";
import { useChatContext } from "../../../services/contexts/ChatContext";
import { CenteredInfinityLoader } from "../../molecules/Loaders/InfinityLoader";
import TopicPill, { SimplePill } from "./TopicPill";
import { PopularTopicsCompanionWidgetQuery as IQuery } from "./__generated__/PopularTopicsCompanionWidgetQuery.graphql";

interface Translation {
  language: string;
  starter: string;
}

interface ConversationStarter {
  id: string;
  key: string;
  title: string;
  starter: string;
  emoji: string | null;
  careType: string;
  translations?: ReadonlyArray<Translation | null> | null;
}

export const PopularTopicsQuery = graphql`
  query PopularTopicsCompanionWidgetQuery(
    $platforms: [ConversationStarterPlatform!]
  ) {
    conversationStarters(isActive: true, platforms: $platforms) {
      nodes {
        id
        key
        title
        starter
        emoji
        careType
        translations {
          language
          starter
        }
      }
    }
  }
`;

interface PopularTopicsContentProps {
  queryReference: PreloadedQuery<IQuery>;
  from?: string;
  threadType?: "WORK" | "WELLBEING";
}

function PopularTopicsContent({
  queryReference,
  from,
  threadType,
}: PopularTopicsContentProps) {
  const { t } = useTranslation("companion", {
    keyPrefix: "popularTopicsCompanionWidget",
  });
  const { data: user } = useUserData();
  const { trackEvent } = useAmplitude();
  const { createPopularTopicsThread } = useChatContext();
  const [isMounted, setIsMounted] = useState<boolean>(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const data = usePreloadedQuery<IQuery>(PopularTopicsQuery, queryReference);

  const getTranslatedStarter = (
    starter: ConversationStarter,
    preferredLanguage?: string | null,
  ): string => {
    if (!preferredLanguage || preferredLanguage === "en") {
      return starter.starter;
    }

    const translation = starter.translations?.find(
      (trans): trans is Translation =>
        trans !== null && trans.language === preferredLanguage,
    );

    return translation?.starter || starter.starter;
  };

  const topics: SimplePill[] = useMemo(() => {
    return (data.conversationStarters.nodes || []).map((starter) => ({
      key: starter.key,
      title: starter.title,
      starter: starter.starter,
      icon: starter.emoji || "",
      careType: starter.careType,
      starterWithTranslations: starter as ConversationStarter,
    }));
  }, [data.conversationStarters.nodes]);

  const handlePillClick = (
    pill: SimplePill & { starterWithTranslations?: ConversationStarter },
  ) => {
    const translatedStarter: string = pill.starterWithTranslations
      ? getTranslatedStarter(
          pill.starterWithTranslations,
          user?.preferredLanguage,
        )
      : pill.starter || "";

    const careModality =
      pill.careType === "THERAPEUTIC_STRUCTURED"
        ? "THERAPY"
        : "PROFESSIONAL_COACHING";

    if (from === "oliva_at_work") {
      trackEvent("Playbook clicked", {
        title: pill.title,
        section: "Popular Topics",
        from: "home",
      });
    } else {
      trackEvent("Oli conversation starter clicked", {
        topic: pill.title,
        type: careModality,
        from,
      });
    }

    const trackFrom =
      from === "oliva_at_work"
        ? CREATE_THREAD_TRACK_FROM.OLIVA_AT_WORK
        : CREATE_THREAD_TRACK_FROM.POPULAR_TOPICS;

    createPopularTopicsThread(
      translatedStarter,
      careModality,
      trackFrom,
      undefined,
      threadType,
    );
  };

  return (
    <div>
      {from !== "oliva_at_work" && (
        <Text align="center" gutter="md" weight="bold" size="xs" color="white">
          {t("title")}
        </Text>
      )}
      <div className="flex flex-wrap gap-2 justify-center items-center">
        {topics.map((pill, index) => {
          const shouldAnimate = isMounted;
          const delay = `${index * 100}ms`;

          return (
            <TopicPill
              key={`pill-${pill.key}`}
              pillKey={pill.key}
              pill={pill}
              shouldAnimate={shouldAnimate}
              animationDelay={delay}
              onClick={(_, pill) => handlePillClick(pill)}
            />
          );
        })}
      </div>
    </div>
  );
}

interface PopularTopicsCompanionWidgetProps {
  from?: string;
  threadType?: "WORK" | "WELLBEING";
}

export default function PopularTopicsCompanionWidget({
  from,
  threadType,
}: PopularTopicsCompanionWidgetProps) {
  const [queryReference, loadQuery] =
    useQueryLoader<IQuery>(PopularTopicsQuery);

  useEffect(() => {
    const platforms: ConversationStarterPlatform[] | undefined =
      from === "oliva_at_work"
        ? [ConversationStarterPlatform.OLIATWORK_HOME]
        : [ConversationStarterPlatform.WEB];
    loadQuery({ platforms });
  }, [loadQuery, from]);

  return (
    <Suspense
      fallback={
        <CenteredInfinityLoader
          height={from === "oliva_at_work" ? "full" : "screen"}
        />
      }
    >
      {queryReference && (
        <PopularTopicsContent
          queryReference={queryReference}
          from={from}
          threadType={threadType}
        />
      )}
    </Suspense>
  );
}
